import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Package,
  Printer,
  FileText,
  Users,
  Receipt,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  DollarSign,
  Settings,
  ClipboardCheck,
  Calculator,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";

const NAV_SECTIONS = [
  {
    key: "inventory",
    label: "Inventory",
    icon: Package,
    items: [
      { key: "daily-inventory", label: "Daily Inventory Log", permKey: "daily_inventory_log" },
      { key: "master-inventory", label: "Master Inventory", permKey: "master_inventory" },
      { key: "stock-resupply", label: "Stock Resupply", permKey: "stock_resupply" },
      { key: "daily-closing-log", label: "Daily Closing Log", permKey: "daily_closing_log" },
      { key: "weekly-closing-log", label: "Weekly Stock Data", permKey: "weekly_stock_data" },
    ],
  },
  {
    key: "printables",
    label: "Printables",
    icon: Printer,
    items: [
      { key: "menu", label: "Menu" },
      { key: "recipes", label: "Recipes" },
    ],
  },
  {
    key: "documents",
    label: "Documents",
    icon: FileText,
    items: [
      { key: "business-permits", label: "Business Permits" },
      { key: "itr-1701q", label: "ITR (1701Q)" },
      { key: "bir-form-2307", label: "BIR Form 2307" },
      { key: "lease-contracts", label: "Lease Contracts" },
      {
        key: "contributions", label: "Contributions",
        children: [
          { key: "sss", label: "SSS" },
          { key: "pag-ibig", label: "PAG-IBIG" },
          { key: "philhealth", label: "PhilHealth" },
        ],
      },
      {
        key: "employees-documents", label: "Employees Documents",
        children: [
          { key: "emp-probationary-contract", label: "Probationary Contract" },
          { key: "emp-full-contract", label: "Full Contract" },
          { key: "emp-resignation-letter", label: "Resignation Letter" },
          { key: "emp-termination", label: "Termination" },
          { key: "emp-nte", label: "NTE" },
          { key: "emp-payroll-docs", label: "Payroll Docs" },
          { key: "emp-memo", label: "Memo" },
          { key: "emp-handbook", label: "Knto Cinco Handbook" },
        ],
      },
    ],
    branches: true,
  },
  {
    key: "bills",
    label: "Bills",
    icon: Receipt,
    branches: true,
    items: [
      { key: "water", label: "Water" },
      { key: "electricity", label: "Electricity" },
      { key: "internet", label: "Internet" },
      { key: "pos", label: "POS" },
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    icon: DollarSign,
    items: [
      { key: "staff-payroll", label: "Staff Payroll" },
      { key: "13th-month", label: "13th Month Payroll" },
    ],
  },
  {
    key: "sales",
    label: "Sales",
    icon: TrendingUp,
    items: [
      { key: "expenses", label: "Expenses" },
      { key: "price-trends", label: "Price Trends" },
      { key: "monthly-sales-report", label: "Monthly Sales Report" },
    ],
  },
  {
    key: "recipe-costing",
    label: "Food Recipe Costing",
    icon: Calculator,
    items: [
      { key: "calculator", label: "Recipe Calculator" },
    ],
  },
  {
    key: "reconciliation",
    label: "Drink Reconciliation",
    icon: ClipboardCheck,
    items: [
      { key: "report", label: "Reconciliation Report" },
      { key: "mappings", label: "POS Drink Mapping" },
      { key: "recipes", label: "Recipe Usage" },
    ],
  },
];

function SidebarNav({ onItemClick, isMobile }) {
  const navigate = useNavigate();
  const { hasPermission, hasSubPermission, isMasterAdmin } = useAuth();
  const [expandedSections, setExpandedSections] = useState(["inventory"]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [expandedBranches, setExpandedBranches] = useState([]);

  const visibleSections = NAV_SECTIONS.filter(s => hasPermission(s.key, "view"));

  const toggleSection = (key) => {
    const section = NAV_SECTIONS.find(s => s.key === key);
    if (isMobile && !section?.branches && !section?.items?.length) {
      navigate(`/dashboard/${key}`);
      if (onItemClick) onItemClick();
    } else {
      setExpandedSections((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    }
  };

  const toggleItem = (key) => {
    setExpandedItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleBranch = (key) => {
    setExpandedBranches((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const renderNavItem = (item, sectionKey, branch) => {
    const hasChildren = item.children && item.children.length > 0;
    const itemExpandKey = branch ? `${branch}-${item.key}` : item.key;
    const isItemExpanded = expandedItems.includes(itemExpandKey);

    if (hasChildren) {
      return (
        <div key={itemExpandKey}>
          <button
            data-testid={`nav-item-${itemExpandKey}`}
            onClick={() => toggleItem(itemExpandKey)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50`}
          >
            <span>{item.label}</span>
            {isItemExpanded ? (
              <ChevronDown className="w-3 h-3 text-zinc-400" />
            ) : (
              <ChevronRight className="w-3 h-3 text-zinc-400" />
            )}
          </button>
          {isItemExpanded && (
            <div className="ml-3 pl-3 border-l border-zinc-100 space-y-0.5">
              {item.children.map((child) => renderNavItem(child, sectionKey, branch))}
            </div>
          )}
        </div>
      );
    }

    const linkPath = branch
      ? `/dashboard/${sectionKey}/${branch}/${item.key}`
      : `/dashboard/${sectionKey}/${item.key}`;

    return (
      <NavLink
        key={itemExpandKey}
        to={linkPath}
        data-testid={`nav-item-${itemExpandKey}`}
        onClick={onItemClick}
        className={({ isActive }) =>
          `block px-3 py-2 rounded-lg text-sm transition-colors ${
            isActive
              ? "bg-zinc-100 text-zinc-900 font-medium"
              : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <ScrollArea className="flex-1">
      <nav className="px-3 py-4 space-y-1" data-testid="sidebar-nav">
        {visibleSections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.key);

          return (
            <div key={section.key} className="space-y-0.5">
              <button
                data-testid={`nav-section-${section.key}`}
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left">{section.label}</span>
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
                )}
              </button>

              {isExpanded && (
                <div className="ml-4 pl-3 border-l border-zinc-200 space-y-0.5">
                  {section.branches ? (
                    <>
                      {["puan", "lapu-lapu", "office"].map((branch) => {
                        const branchLabel = branch === "puan" ? "Puan Branch" : branch === "lapu-lapu" ? "Lapu-Lapu Branch" : "Office";
                        const branchKey = `${section.key}-${branch}`;
                        const isBranchExpanded = expandedBranches.includes(branchKey);
                        return (
                          <div key={branchKey}>
                            <button
                              data-testid={`nav-branch-${branchKey}`}
                              onClick={() => toggleBranch(branchKey)}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors text-zinc-600 hover:text-zinc-800 hover:bg-zinc-50"
                            >
                              <span>{branchLabel}</span>
                              {isBranchExpanded ? (
                                <ChevronDown className="w-3 h-3 text-zinc-400" />
                              ) : (
                                <ChevronRight className="w-3 h-3 text-zinc-400" />
                              )}
                            </button>
                            {isBranchExpanded && (
                              <div className="ml-3 pl-3 border-l border-zinc-100 space-y-0.5">
                                {section.items
                                  .filter(item => !item.permKey || hasSubPermission(section.key, item.permKey, "view"))
                                  .map((item) => renderNavItem(item, section.key, branch))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    section.items
                      .filter(item => !item.permKey || hasSubPermission(section.key, item.permKey, "view"))
                      .map((item) => renderNavItem(item, section.key))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      {(isMasterAdmin || hasPermission("admin_settings", "view")) && (
        <div className="mt-4 pt-4 border-t border-zinc-100 px-2">
          <NavLink
            to="/dashboard/admin-settings"
            data-testid="nav-admin-settings"
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
              }`
            }
          >
            <Settings className="w-4 h-4" />
            Admin Settings
          </NavLink>
        </div>
      )}
    </ScrollArea>
  );
}

export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className="w-64 bg-white border-r border-zinc-200 h-[calc(100vh-64px)] fixed left-0 top-16 z-20 hidden md:flex flex-col"
        data-testid="sidebar-desktop"
      >
        <SidebarNav isMobile={false} />
      </aside>

      {/* Mobile Toggle */}
      <div className="md:hidden fixed bottom-4 left-4 z-50">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              size="icon"
              data-testid="sidebar-mobile-toggle"
              className="rounded-full w-12 h-12 bg-zinc-900 text-white shadow-lg hover:bg-zinc-800"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 pt-4">
            <SidebarNav isMobile={true} onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}

export { NAV_SECTIONS };
